'use client';
import { useCallback, useMemo, useState } from 'react';
import { useActiveControl } from '../useActiveControl';
import { useInvariantCallbackRef } from '../useInvariantCallbackRef';
import { v4 } from 'uuid';
export const useDrawer = ({ id, onOpen, onClose, initialActive, 
// Some optional Drawer props
position = 'right', size = 'default', returnFocusOnDeactivate = false, hasBackdrop = false, width } = {}) => {
    const drawerControl = useActiveControl({ initialActive });
    const { isActive, open, close } = drawerControl;
    const drawerId = id ?? `a_${v4()}`;
    // track if drawer is opened from another drawer
    const [isChild, setIsChild] = useState(false);
    /**
     * Controllers
     */
    const invariantOnOpenRef = useInvariantCallbackRef(onOpen);
    const openDrawer = useCallback((_e = null, isChild = false) => {
        setIsChild(isChild);
        open();
        invariantOnOpenRef.current();
    }, [open, invariantOnOpenRef]);
    const invariantOnCloseRef = useInvariantCallbackRef(onClose);
    const closeDrawer = useCallback(() => {
        close();
        invariantOnCloseRef.current();
        setIsChild(false);
    }, [close, invariantOnCloseRef]);
    /**
     * Initiator Props
     */
    const initiatorProps = useMemo(() => ({
        'aria-controls': drawerId,
        'aria-expanded': isActive,
        onClick: openDrawer
    }), [drawerId, isActive, openDrawer]);
    /**
     * catDrawerProps
     */
    const drawerProps = useMemo(() => ({
        id: drawerId,
        isActive,
        onBlDrawerClose: (e) => {
            e.stopPropagation();
            closeDrawer();
        },
        // Other optional props
        position,
        hasBackdrop,
        size,
        returnFocusOnDeactivate,
        width
    }), [
        drawerId,
        isActive,
        position,
        hasBackdrop,
        size,
        returnFocusOnDeactivate,
        width,
        closeDrawer
    ]);
    return useMemo(() => ({
        initiatorProps,
        drawerProps,
        isDrawerOpen: isActive,
        openDrawer,
        closeDrawer,
        isChild,
        // Deprecated props
        /**
         * This value will be removed soon, please use `isDrawerOpen`, `openDrawer` and `closeDrawer` values
         * @deprecated
         */
        drawerControl,
        /**
         * This value will be removed soon, please use `openDrawer` function
         * @deprecated
         */
        handleOpen: openDrawer,
        /**
         * This value will be removed soon, please use `closeDrawer` function
         * @deprecated
         */
        handleClose: closeDrawer
    }), [
        initiatorProps,
        drawerProps,
        isActive,
        openDrawer,
        closeDrawer,
        isChild,
        drawerControl
    ]);
};
